import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInit = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInitialize = async () => {
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/initialize?email=${encodeURIComponent(email)}`
      );
      
      setSuccess(true);
      toast.success(response.data.message);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('User not found. Please login with Google first, then use this page to promote to admin.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to initialize admin');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Card className="glass border-white/20 max-w-md w-full mx-4">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Shield className="w-16 h-16 text-white" />
          </div>
          <CardTitle className="text-3xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Admin Initialization
          </CardTitle>
          <CardDescription className="text-white/80">
            Promote a user account to master admin with unlimited access
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!success ? (
            <>
              <div className="space-y-2">
                <label className="text-white text-sm font-medium">Email Address</label>
                <Input
                  data-testid="admin-email-input"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleInitialize()}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
              </div>
              
              <div className="bg-white/10 p-4 rounded-lg">
                <p className="text-white/90 text-sm">
                  <strong>Note:</strong> The user must login with Google first before they can be promoted to admin.
                </p>
              </div>

              <Button
                data-testid="initialize-admin-button"
                onClick={handleInitialize}
                disabled={loading}
                className="w-full bg-white text-purple-700 hover:bg-gray-100 py-6 text-lg font-semibold"
              >
                {loading ? 'Processing...' : 'Initialize Admin'}
              </Button>

              <div className="text-center">
                <a href="/" className="text-white/80 hover:text-white text-sm underline">
                  Back to Home
                </a>
              </div>
            </>
          ) : (
            <div className="text-center space-y-6">
              <CheckCircle className="w-16 h-16 mx-auto text-green-400" />
              <div>
                <p className="text-white text-lg font-semibold">Admin Initialized Successfully!</p>
                <p className="text-white/80 text-sm mt-2">{email} is now a master admin</p>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={() => window.location.href = '/'}
                  className="w-full bg-white text-purple-700 hover:bg-gray-100"
                >
                  Go to Login
                </Button>
                <Button
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Initialize Another Admin
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminInit;
